
import { memo, useState } from 'react';
import { CheckCircle2, ChevronDown, CircleDashed, Trash2, XCircle } from 'lucide-react';
import type {
  ApprovalLevelProgress,
  ApprovalRoundProgress,
  TicketApproval,
} from '@/entities/ticket';
import { ApprovalIcon } from '@/shared/config';
import { Badge, Button, IconButton, Textarea, Tooltip } from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import { ApprovalRow } from '@/widgets/ApprovalRow';
import { getUserDisplayName } from './lib';
import styles from './TicketDetail.module.css';

const ROUND_STATUS_LABELS: Record<ApprovalRoundProgress['status'], string> = {
  pending: 'идёт',
  approved: 'согласовано',
  rejected: 'не согласовано',
  cancelled: 'снято',
};

const LevelIcon: React.FC<{ outcome: ApprovalLevelProgress['outcome'] }> = ({ outcome }) => {
  if (outcome === 'passed') return <CheckCircle2 size={14} aria-hidden />;
  if (outcome === 'failed') return <XCircle size={14} aria-hidden />;
  return <CircleDashed size={14} aria-hidden />;
};

function levelSummary(level: ApprovalLevelProgress): string {
  const base = `${level.approved} из ${level.required}`;
  if (level.outcome === 'passed') return `${base} · кворум набран`;
  if (level.outcome === 'failed') return `${base} · кворум недостижим`;
  return `${base} · ждём ${level.pending}`;
}

function byPendingFirst(a: TicketApproval, b: TicketApproval): number {
  if (a.status === b.status) return 0;
  if (a.status === 'pending') return -1;
  if (b.status === 'pending') return 1;
  return 0;
}

function approvalsTone(
  live: TicketApproval[],
  round: ApprovalRoundProgress | null,
): 'resolutionDanger' | 'resolutionPending' | 'resolutionDone' | 'satisfactionNeutral' {
  if (round?.status === 'rejected' || live.some((a) => a.status === 'rejected')) {
    return 'resolutionDanger';
  }
  if (round?.status === 'pending' || live.some((a) => a.status === 'pending')) {
    return 'resolutionPending';
  }
  return live.length > 0 ? 'resolutionDone' : 'satisfactionNeutral';
}

export interface ApprovalsSectionProps {
  round: ApprovalRoundProgress | null;
  approvals: TicketApproval[];
  currentUserId: string | undefined;
  loadingId: string | null;
  canRemove: boolean;
  canViewProfiles: boolean;
  removingId: string | null;
  removingRound: boolean;
  onApprove: (approvalId: string, comment: string) => void;
  onReject: (approvalId: string, comment: string) => void;
  onRemove: (approvalId: string) => void;
  onRemoveRound: (roundId: string) => void;
  liftedApprovalId?: string | null;
}

const ApprovalsSectionBase: React.FC<ApprovalsSectionProps> = ({
  round,
  approvals,
  currentUserId,
  loadingId,
  canRemove,
  canViewProfiles,
  removingId,
  removingRound,
  onApprove,
  onReject,
  onRemove,
  onRemoveRound,
  liftedApprovalId,
}) => {
  const [comments, setComments] = useState<Record<string, string>>({});
  const [showCancelled, setShowCancelled] = useState(false);

  const roundLevels = round?.levels ?? [];
  const inRound = (a: TicketApproval) => Boolean(round) && a.roundId === round!.roundId;
  const cancelledCount = approvals.filter((a) => a.status === 'cancelled').length;
  const visible = (a: TicketApproval) => showCancelled || a.status !== 'cancelled';

  const loose = approvals.filter((a) => !inRound(a)).filter(visible);

  const live = approvals.filter((a) => a.status !== 'cancelled');
  const decided = live.filter((a) => a.status !== 'pending').length;
  const summary =
    live.length === 0
      ? 'все сняты'
      : decided === live.length
        ? 'все решения приняты'
        : `${decided} из ${live.length} — решение принято`;

  const person = (a: TicketApproval, withSubject: boolean) => {
    const name = getUserDisplayName(a.approver, a.approverId);
    const deciding =
      a.status === 'pending' && currentUserId === a.approverId && a.id !== liftedApprovalId;
    return (
      <ApprovalRow
        key={a.id}
        as="li"
        approval={a}
        name={name}
        profileHref={canViewProfiles ? `/users/${a.approverId}` : null}
        subject={withSubject ? a.subject : null}
        actions={
          canRemove && (
            <IconButton
              size="sm"
              tone="danger"
              label={`Удалить согласование: ${name}`}
              tooltip={a.roundId ? 'Удалить круг согласования' : 'Удалить согласование'}
              onClick={() => onRemove(a.id)}
              disabled={removingId === a.id}
            >
              <Trash2 size={14} aria-hidden />
            </IconButton>
          )
        }
      >
        {deciding && (
          <>
            <Textarea
              className={styles.approvalCommentInput}
              placeholder="Комментарий к решению (обязателен при отклонении)"
              value={comments[a.id] ?? ''}
              onChange={(e) => setComments((prev) => ({ ...prev, [a.id]: e.target.value }))}
              rows={2}
            />
            <div className={styles.approvalActions}>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => onApprove(a.id, (comments[a.id] ?? '').trim())}
                disabled={loadingId === a.id}
              >
                Согласовать
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => onReject(a.id, (comments[a.id] ?? '').trim())}
                disabled={loadingId === a.id}
              >
                Отклонить
              </Button>
            </div>
          </>
        )}
      </ApprovalRow>
    );
  };

  return (
    <section
      className={cn(
        styles.resolutionCallout,
        styles[approvalsTone(live, round)],
        styles.approvalsCallout,
      )}
      aria-label="Согласования"
    >
      <div className={styles.resolutionHead}>
        <span className={styles.resolutionIcon} aria-hidden>
          <ApprovalIcon size={16} />
        </span>
        <h2 className={styles.resolutionTitle}>Согласования</h2>
        <span className={styles.resolutionState}>{summary}</span>
        {round && canRemove && (
          <IconButton
            size="sm"
            tone="danger"
            className={styles.approvalsHeadAction}
            label="Удалить согласование комитета"
            tooltip="Удалить согласование комитета"
            onClick={() => onRemoveRound(round.roundId)}
            disabled={removingRound}
          >
            <Trash2 size={14} aria-hidden />
          </IconButton>
        )}
      </div>

      { }
      {round && (
        <p className={styles.approvalCommittee}>
          {round.committeeName ?? 'Комитет'} · {ROUND_STATUS_LABELS[round.status]}
        </p>
      )}

      {roundLevels.map((level) => {
        const people = approvals
          .filter((a) => inRound(a) && a.levelIndex === level.index)
          .filter(visible)
          .sort(byPendingFirst);
        return (
          <div key={level.index} className={styles.approvalLevel}>
            <div className={styles.approvalLevelHead}>
              <span
                className={cn(styles.roundLevelIcon, styles[`roundLevel-${level.outcome}`])}
                aria-hidden
              >
                <LevelIcon outcome={level.outcome} />
              </span>
              <span className={styles.approvalLevelName}>{level.name}</span>
              {level.isOptional && (
                <Tooltip content="Необязательный уровень: его провал не мешает согласованию">
                  <Badge>необязательный</Badge>
                </Tooltip>
              )}
              <span className={styles.approvalLevelSummary}>
                {levelSummary(level)}
                {level.rejected > 0 && ` · отклонили ${level.rejected}`}
              </span>
            </div>
            {people.length > 0 && (
              <ul className={styles.approvalsList}>{people.map((a) => person(a, false))}</ul>
            )}
          </div>
        );
      })}

      {loose.length > 0 && (
        <div className={styles.approvalLevel}>
          {roundLevels.length > 0 && (
            <div className={styles.approvalLevelHead}>
              <span className={styles.approvalLevelName}>Отдельные согласования</span>
            </div>
          )}
          <ul className={styles.approvalsList}>
            {loose.sort(byPendingFirst).map((a) => person(a, true))}
          </ul>
        </div>
      )}

      {cancelledCount > 0 && (
        <button
          type="button"
          className={styles.approvalCancelledToggle}
          aria-expanded={showCancelled}
          onClick={() => setShowCancelled((v) => !v)}
        >
          <ChevronDown
            size={13}
            aria-hidden
            className={cn(styles.approvalChevron, showCancelled && styles.approvalChevronOpen)}
          />
          {showCancelled ? 'Скрыть снятые' : `Показать снятые (${cancelledCount})`}
        </button>
      )}
    </section>
  );
};

export const ApprovalsSection = memo(ApprovalsSectionBase);
